import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";

import { Modal, Button, Form } from "react-bootstrap";
import { createErpSocket } from "../utils/socketIo";
import Select from "react-select";

import toast from "react-hot-toast";
import { getUserData, node_apiUrl } from "./const";
import {
  getErpToken,
  getErpAuthState,
  listTenants,
  listBranches,
  isErpApiSuccess,
} from "../erpClient";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { hasPermission, PERMISSIONS } from "../utils/permissions";

function TenantBranchTable() {
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [tenantList, setTenantList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [tenantLookup, setTenantLookup] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showAddTenantModal, setShowAddTenantModal] = useState(false);
  const [showAddBranchModal, setShowAddBranchModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [socket, setSocket] = useState(null);

  // Tenant form states
  const [tenantName, setTenantName] = useState("");

  // Branch form states
  const [branchTenantId, setBranchTenantId] = useState("");
  const [branchName, setBranchName] = useState("");
  const [branchCode, setBranchCode] = useState("");
  const [branchAddress, setBranchAddress] = useState("");
  const [branchMobile, setBranchMobile] = useState("");
  const [branchGstNumber, setBranchGstNumber] = useState("");
  const [branchPanCard, setBranchPanCard] = useState("");
  const [branchCin, setBranchCin] = useState("");

  const resetBranchForm = () => {
    setBranchTenantId("");
    setBranchName("");
    setBranchCode("");
    setBranchAddress("");
    setBranchMobile("");
    setBranchGstNumber("");
    setBranchPanCard("");
    setBranchCin("");
    setEditingBranch(null);
  };

  const toNullableString = (value) => {
    const trimmed = String(value ?? "").trim();
    return trimmed || null;
  };

  const userData = getUserData();
  const erpAuthState = getErpAuthState();

  const canTenantTable = hasPermission(PERMISSIONS.TENANT_TABLE);
  const canBranchTable = hasPermission(PERMISSIONS.BRANCH_TABLE);
  const canEditTenant = hasPermission(PERMISSIONS.EDIT_TENANT);
  const canEditBranch = hasPermission(PERMISSIONS.EDIT_BRANCH);

  // Fetch tenants
  const fetchTenants = async () => {
    setIsLoading(true);
    try {
      const data = await listTenants();

      if (isErpApiSuccess(data) && Array.isArray(data?.data?.tenants)) {
        const fetchedItems = data.data.tenants.map((tenant) => ({
          id: tenant.id,
          name: tenant.name,
          created_at: tenant.created_at,
        }));

        fetchedItems.sort((a, b) => b.id - a.id);
        setTenantList(fetchedItems);

        // Update tenant options
        const options = fetchedItems.map((tenant) => ({
          value: tenant.id,
          label: tenant.name,
        }));
        setTenantOptions(options);

        const lookup = {};
        fetchedItems.forEach((tenant) => {
          lookup[tenant.id] = tenant.name;
        });
        setTenantLookup(lookup);
      } else {
        setTenantList([]);
        setTenantOptions([]);
        setTenantLookup({});
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
      toast.error("Error fetching tenants");
      setTenantList([]);
      setTenantOptions([]);
      setTenantLookup({});
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const data = await listBranches();

      if (isErpApiSuccess(data) && Array.isArray(data?.data?.branches)) {
        const fetchedItems = data.data.branches.map((branch) => ({
          id: branch.id,
          tenant_id: branch.tenant_id,
          name: branch.name,
          code: branch.code,
          address: branch.address ?? null,
          mobile: branch.mobile ?? null,
          gst_number: branch.gst_number ?? null,
          pan_card: branch.pan_card ?? null,
          cin: branch.cin ?? null,
          updated_at: branch.updated_at,
        }));

        fetchedItems.sort((a, b) => b.id - a.id);
        setBranchList(fetchedItems);
      } else {
        setBranchList([]);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      toast.error("Error fetching branches");
      setBranchList([]);
    }
  };

  // Add useEffect to fetch data when component mounts
  useEffect(() => {
    fetchTenants();
    fetchBranches();
  }, []);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    newSocket.on("connect", () => {
      // console.log("TenantBranchTable: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("TenantBranchTable: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("TenantBranchTable: Socket connection error:", error);
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Handle Edit Branch Click
  const handleEditBranchClick = (branch) => {
    setEditingBranch(branch);
    setBranchTenantId(String(branch.tenant_id));
    setBranchName(branch.name || "");
    setBranchCode(branch.code || "");
    setBranchAddress(branch.address || "");
    setBranchMobile(branch.mobile || "");
    setBranchGstNumber(branch.gst_number || "");
    setBranchPanCard(branch.pan_card || "");
    setBranchCin(branch.cin || "");
    setShowAddBranchModal(true);
  };

  // Handle Page Change
  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(tenantList.length / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle Items Per Page Change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
  };

  // Pagination logic
  const displayedBranches = branchList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Handle Add Tenant Form Submission
  const handleTenantSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getErpToken();

      const response = await fetch(`${node_apiUrl}/tenant/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: "create",
          name: tenantName,
        }),
      });

      const data = await response.json();

      if (data?.success) {
        toast.success("Tenant added successfully");
        setShowAddTenantModal(false);
        setTenantName("");
        fetchTenants();
      } else {
        toast.error(data?.message || "Failed to add tenant");
      }
    } catch (error) {
      console.error("Error submitting tenant form:", error);
      toast.error(error.message || "Failed to add tenant");
    }
  };

  // Handle Add/Edit Branch Form Submission
  const handleBranchSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getErpToken();
      const isEdit = editingBranch !== null;
      const tenantId = isEdit
        ? Number(editingBranch.tenant_id)
        : Number(branchTenantId);

      if (!isEdit && (!tenantId || Number.isNaN(tenantId))) {
        toast.error("Please select a tenant");
        return;
      }

      const branchId = isEdit ? Number(editingBranch.id) : null;
      if (isEdit && (!branchId || Number.isNaN(branchId))) {
        toast.error("Invalid branch");
        return;
      }

      const response = await fetch(`${node_apiUrl}/branch/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: isEdit ? "update" : "create",
          tenant_id: tenantId,
          ...(isEdit ? { branch_id: branchId, id: branchId } : {}),
          name: String(branchName || "").trim(),
          code: String(branchCode || "").trim(),
          address: toNullableString(branchAddress),
          mobile: toNullableString(branchMobile),
          gst_number: toNullableString(branchGstNumber),
          pan_card: toNullableString(branchPanCard),
          cin: toNullableString(branchCin),
        }),
      });

      const data = await response.json();

      if (data?.success) {
        toast.success(
          isEdit ? "Branch updated successfully" : "Branch added successfully",
        );
        setShowAddBranchModal(false);

        resetBranchForm();

        // Refresh lists
        fetchBranches();
      } else {
        toast.error(
          data?.message ||
            (isEdit ? "Failed to update branch" : "Failed to add branch"),
        );
      }
    } catch (error) {
      console.error("Error submitting branch form:", error);
      toast.error(
        error.message ||
          (editingBranch ? "Failed to update branch" : "Failed to add branch"),
      );
    }
  };

  // Reset tenant form fields when modal closes
  const handleCloseTenantModal = () => {
    setShowAddTenantModal(false);
    setTenantName("");
  };

  // Reset branch form fields when modal closes
  const handleCloseBranchModal = () => {
    setShowAddBranchModal(false);
    resetBranchForm();
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Tenant & Branch</h4>
            {(canTenantTable || canBranchTable) && (
              <div className="d-flex gap-2">
                {canTenantTable && (
                  <button
                    type="button"
                    className={`btn btn-primary ${buttonClass}`}
                    disabled={!canEditTenant}
                    aria-disabled={!canEditTenant}
                    title={
                      canEditTenant
                        ? "Add tenant"
                        : "You don't have permission to add a tenant"
                    }
                    onClick={() => {
                      if (!canEditTenant) return;
                      setShowAddTenantModal(true);
                    }}
                  >
                    Add Tenant
                  </button>
                )}
                {canBranchTable && (
                  <button
                    type="button"
                    className={`btn btn-success ${buttonClass}`}
                    disabled={!canEditBranch}
                    aria-disabled={!canEditBranch}
                    title={
                      canEditBranch
                        ? "Add branch"
                        : "You don't have permission to add a branch"
                    }
                    onClick={() => {
                      if (!canEditBranch) return;
                      setShowAddBranchModal(true);
                    }}
                  >
                    Add Branch
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="card-body p-4">
            {/* Search Section */}
            <div className="d-flex justify-content-between mb-4">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Tenant and Branch Sections */}
            <div className="row">
              {tenantList.map((tenant) => (
                <div key={tenant.id} className="col-md-6 mb-4">
                  <div className={`border rounded p-3 ${cardClass}`}>
                    {(canTenantTable || canBranchTable) && (
                      <>
                        <div className="mb-3">
                          <h5 className="mb-3 fw-bold">
                            {canTenantTable ? "Tenant Name" : "Tenant"}
                          </h5>
                          <span className="fs-5">{tenant.name}</span>
                        </div>

                        {canBranchTable && (
                          <>
                            <h6 className="mb-2 fw-semibold">Branch's Table</h6>
                            <div
                              className={`table-responsive border ${cardClass}`}
                            >
                              <table
                                className={`table table-sm mb-0 ${tableClass}`}
                              >
                                <thead>
                                  <tr>
                                    <th className="border-bottom">
                                      Branch Name
                                    </th>
                                    <th className="border-bottom">Code</th>
                                    <th className="border-bottom">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {branchList
                                    .filter(
                                      (branch) =>
                                        Number(branch.tenant_id) ===
                                        Number(tenant.id),
                                    )
                                    .map((branch) => (
                                      <tr key={branch.id}>
                                        <td>{branch.name || "N/A"}</td>
                                        <td>{branch.code || "N/A"}</td>
                                        <td>
                                          <button
                                            type="button"
                                            className={`btn btn-primary btn-sm rounded-1 ${buttonClass}`}
                                            onClick={() =>
                                              handleEditBranchClick(branch)
                                            }
                                            disabled={!canEditBranch}
                                            aria-disabled={!canEditBranch}
                                            title={
                                              canEditBranch
                                                ? "Edit branch"
                                                : "You don't have permission to edit"
                                            }
                                          >
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              aria-hidden="true"
                                              role="img"
                                              className="iconify iconify--solar"
                                              width="14"
                                              height="14"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                fill="currentColor"
                                                d="m11.4 18.161l7.396-7.396a10.3 10.3 0 0 1-3.326-2.234a10.3 10.3 0 0 1-2.235-3.327L5.839 12.6c-.577.577-.866.866-1.114 1.184a6.6 6.6 0 0 0-.749 1.211c-.173.364-.302.752-.56 1.526l-1.362 4.083a1.06 1.06 0 0 0 1.342 1.342l4.083-1.362c.775-.258 1.162-.387 1.526-.56q.647-.308 1.211-.749c.318-.248.607-.537 1.184-1.114m9.448-9.448a3.932 3.932 0 0 0-5.561-5.561l-.887.887l.038.111a8.75 8.75 0 0 0 2.092 3.32a8.75 8.75 0 0 0 3.431 2.13z"
                                              />
                                            </svg>
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add Tenant Modal */}
      <Modal
        show={showAddTenantModal}
        onHide={handleCloseTenantModal}
        centered
        contentClassName={cardClass}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Tenant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form noValidate="" onSubmit={handleTenantSubmit}>
            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">
                  Tenant Name 
                </label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  required
                  placeholder="Enter Tenant Name"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                />
              </div>
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <button
                type="submit"
                className={`btn btn-primary ${buttonClass}`}
              >
                Submit
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Add/Edit Branch Modal */}
      <Modal
        show={showAddBranchModal}
        onHide={handleCloseBranchModal}
        centered
        contentClassName={cardClass}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingBranch ? "Edit Branch" : "Add Branch"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form noValidate="" onSubmit={handleBranchSubmit}>
            {!editingBranch && (
              <div className="row">
                <div className="mb-3 col-md-12">
                  <label className="form-label">
                    Tenant 
                  </label>
                  <Select
                    value={
                      branchTenantId
                        ? {
                            value: branchTenantId,
                            label:
                              tenantLookup[branchTenantId] || branchTenantId,
                          }
                        : null
                    }
                    onChange={(selectedOption) => {
                      setBranchTenantId(
                        selectedOption ? String(selectedOption.value) : "",
                      );
                    }}
                    options={tenantOptions.map((option) => ({
                      ...option,
                      value: String(option.value),
                    }))}
                    placeholder="Select Tenant"
                    isSearchable
                    menuPortalTarget={document.body}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        fontSize: "14px",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    noOptionsMessage={() =>
                      tenantOptions.length === 0
                        ? "Loading options..."
                        : "No options found"
                    }
                  />
                </div>
              </div>
            )}

            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">
                  Branch Name 
                </label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  required
                  placeholder="Enter Branch Name"
                  value={branchName}
                  onChange={(e) => setBranchName(e.target.value)}
                />
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-6">
                <label className="form-label">
                  Branch Code 
                </label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  required
                  placeholder="Enter Branch Code"
                  value={branchCode}
                  onChange={(e) => setBranchCode(e.target.value)}
                />
              </div>
              <div className="mb-3 col-md-6">
                <label className="form-label">Mobile</label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  placeholder="Enter Mobile"
                  value={branchMobile}
                  onChange={(e) => setBranchMobile(e.target.value)}
                />
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">Address</label>
                <textarea
                  className={`form-control ${buttonClass}`}
                  rows={2}
                  placeholder="Enter Address"
                  value={branchAddress}
                  onChange={(e) => setBranchAddress(e.target.value)}
                />
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-4">
                <label className="form-label">GST Number</label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  placeholder="Enter GST Number"
                  value={branchGstNumber}
                  onChange={(e) => setBranchGstNumber(e.target.value)}
                />
              </div>
              <div className="mb-3 col-md-4">
                <label className="form-label">PAN Card</label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  placeholder="Enter PAN Card"
                  value={branchPanCard}
                  onChange={(e) => setBranchPanCard(e.target.value)}
                />
              </div>
              <div className="mb-3 col-md-4">
                <label className="form-label">CIN</label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  placeholder="Enter CIN"
                  value={branchCin}
                  onChange={(e) => setBranchCin(e.target.value)}
                />
              </div>
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <button
                type="submit"
                className={`btn btn-primary ${buttonClass}`}
              >
                Submit
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default TenantBranchTable;
